import { apiClient } from '../lib/api';
import type {
  ChatRoom,
  ChatMessage,
  ChatAttachment,
  ChatUnreadSummary,
  CreateRoomRequest,
  UpdateRoomRequest,
  SendMessageRequest,
  EditMessageRequest,
  MarkReadRequest,
  ListRoomsParams,
  ListMessagesParams,
} from '../types/chat';

interface Envelope<T> {
  status: 'success';
  data: T;
}

interface PaginatedEnvelope<T> {
  status: 'success';
  data: T[];
  meta?: { total: number; page: number; per_page: number; pages: number };
}

// Backend uses two slightly different paginated shapes — list endpoints
// return `data` as the array directly, message endpoints nest it as
// `data.messages`, and attachments nest it as `data.items` plus a
// `pagination` object.
interface MessageListEnvelope {
  status: 'success';
  data: { messages: ChatMessage[] };
}

interface SingleMessageEnvelope {
  status: 'success';
  data: { message: ChatMessage };
}

interface AttachmentListEnvelope {
  status: 'success';
  data: {
    items: ChatAttachment[];
    pagination?: {
      page: number;
      per_page: number;
      total_items: number;
      total_pages: number;
      has_next: boolean;
      has_prev: boolean;
    };
  };
}

function buildQuery(params: object): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'boolean') search.set(k, v ? 'true' : 'false');
    else search.set(k, String(v));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// ── Rooms ───────────────────────────────────────────────────────────────────

export async function listRooms(params: ListRoomsParams = {}): Promise<{
  rooms: ChatRoom[];
  meta?: { total: number; page: number; per_page: number; pages: number };
}> {
  const res = await apiClient.get<PaginatedEnvelope<ChatRoom>>(`/chat/rooms${buildQuery(params)}`);
  return { rooms: res.data, meta: res.meta };
}

export async function getRoom(roomId: string): Promise<ChatRoom> {
  const res = await apiClient.get<Envelope<ChatRoom>>(`/chat/rooms/${roomId}`);
  return res.data;
}

export async function createRoom(req: CreateRoomRequest): Promise<ChatRoom> {
  const res = await apiClient.post<Envelope<ChatRoom>>('/chat/rooms', req);
  return res.data;
}

export async function updateRoom(roomId: string, req: UpdateRoomRequest): Promise<ChatRoom> {
  const res = await apiClient.put<Envelope<ChatRoom>>(`/chat/rooms/${roomId}`, req);
  return res.data;
}

export async function leaveRoom(roomId: string): Promise<void> {
  await apiClient.delete(`/chat/rooms/${roomId}`);
}

export async function addMembers(roomId: string, userIds: string[]): Promise<ChatRoom> {
  const res = await apiClient.post<Envelope<ChatRoom>>(`/chat/rooms/${roomId}/members`, {
    user_ids: userIds,
  });
  return res.data;
}

export async function removeMember(roomId: string, userId: string): Promise<void> {
  await apiClient.delete(`/chat/rooms/${roomId}/members/${userId}`);
}

// ── Messages ────────────────────────────────────────────────────────────────

export async function listMessages(
  roomId: string,
  params: ListMessagesParams = {}
): Promise<ChatMessage[]> {
  const res = await apiClient.get<MessageListEnvelope>(
    `/chat/rooms/${roomId}/messages${buildQuery(params)}`
  );
  return res.data?.messages ?? [];
}

export async function listUnreadMessages(roomId: string): Promise<ChatMessage[]> {
  const res = await apiClient.get<MessageListEnvelope>(
    `/chat/rooms/${roomId}/messages/unread`
  );
  return res.data?.messages ?? [];
}

export async function sendMessage(
  roomId: string,
  req: SendMessageRequest
): Promise<ChatMessage> {
  const res = await apiClient.post<SingleMessageEnvelope>(
    `/chat/rooms/${roomId}/messages`,
    req
  );
  return res.data.message;
}

export async function editMessage(
  roomId: string,
  messageId: string,
  req: EditMessageRequest
): Promise<ChatMessage> {
  const res = await apiClient.put<SingleMessageEnvelope>(
    `/chat/rooms/${roomId}/messages/${messageId}`,
    req
  );
  return res.data.message;
}

export async function deleteMessage(roomId: string, messageId: string): Promise<void> {
  await apiClient.delete(`/chat/rooms/${roomId}/messages/${messageId}`);
}

export async function markRead(roomId: string, req: MarkReadRequest = {}): Promise<void> {
  await apiClient.post(`/chat/rooms/${roomId}/read`, req);
}

// ── Attachments ─────────────────────────────────────────────────────────────

export async function listAttachments(
  roomId: string,
  page = 1,
  perPage = 20
): Promise<ChatAttachment[]> {
  const res = await apiClient.get<AttachmentListEnvelope>(
    `/chat/rooms/${roomId}/attachments?page=${page}&per_page=${perPage}`
  );
  return res.data?.items ?? [];
}

// ── Unread summary ──────────────────────────────────────────────────────────

export async function getUnreadSummary(): Promise<ChatUnreadSummary> {
  const res = await apiClient.get<Envelope<ChatUnreadSummary>>('/chat/unread');
  return res.data;
}
